import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppBadge, AppButton, AppCard } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

type Notification = {
  id: number;
  phone: string;
  type: string;
  subject: string | null;
  message: string;
  sent: boolean;
  sent_at: string | null;
  delivered: boolean;
  provider: string | null;
  created_at: string;
};

export function NotificationLog() {
  const [notifs, setNotifs] = useState<Notification[]>([]);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [resendBusy, setResendBusy] = useState<number | null>(null);
  const PER_PAGE = 30;

  const load = async () => {
    setPageState("loading");
    try {
      const d = await api.get<{ notifications: Notification[]; total: number }>(
        `/notifications?page=${page}&per_page=${PER_PAGE}`,
      );
      setNotifs(d.notifications ?? []);
      setTotal(d.total ?? 0);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load notifications");
      setPageState("error");
    }
  };
  useEffect(() => { load(); }, [page]);

  const resend = async (id: number) => {
    setResendBusy(id);
    try { await api.post(`/notifications/${id}/resend`, {}); await load(); }
    finally { setResendBusy(null); }
  };

  const canResend = (n: Notification) => !n.sent;

  const columns: ColumnDef<Notification, unknown>[] = [
    { id: "created_at", header: "When", accessorFn: (r) => new Date(r.created_at).toLocaleString("en-GB") },
    { accessorKey: "phone", header: "Recipient" },
    { id: "type", header: "Type", cell: ({ row }) => <AppBadge tone="info">{row.original.type}</AppBadge> },
    { id: "message", header: "Message", accessorFn: (r) => {
      const msg = r.message ?? "";
      return msg.substring(0, 60) + (msg.length > 60 ? "…" : "");
    }},
    { id: "status", header: "Status", cell: ({ row }) => (
      <AppBadge tone={row.original.sent ? "success" : "error"}>
        {row.original.sent && <IconCheck size={12}/>}
        {row.original.sent ? "SENT" : "FAILED"}
      </AppBadge>
    )},
    { accessorKey: "provider", header: "Provider" },
    { id: "actions", header: "", enableSorting: false, cell: ({ row }) => (
      canResend(row.original)
        ? <AppButton type="button" variant="secondary" disabled={resendBusy === row.original.id}
            onClick={(e) => { e.stopPropagation(); resend(row.original.id); }}>
            {resendBusy === row.original.id ? <IconSpinner size={12}/> : "Resend"}
          </AppButton>
        : null
    )},
  ];

  const totalPages = Math.ceil(total / PER_PAGE);
  const listStatus =
    pageState === "success" && notifs.length === 0 ? "empty" : pageState;

  return (
    <>
      <PageHeader
        title="Notification Log"
        subtitle={`${total} message${total !== 1 ? "s" : ""}`}
      />

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No notifications"
        emptyHint="Sent messages will appear here."
      >
        <AppCard>
          <AppDataTable data={notifs} columns={columns} />
          {totalPages > 1 && (
            <div className="pg-pagination">
              <AppButton type="button" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</AppButton>
              <span>Page {page} of {totalPages}</span>
              <AppButton type="button" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</AppButton>
            </div>
          )}
        </AppCard>
      </PageState>
    </>
  );
}
